import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Md5 } from 'ts-md5';

import { Observable } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';

import { CoreModule } from '@atmi/core/core.module';
import { basePath } from '@atmi/core/injection';
import {
  CardD,
  cardF,
  CardMI,
  cardMI,
  cardOA,
  cardS,
  ResponsePaginateSuccess,
  ResponseSuccess,
  SourceBibCard
} from '@atmi/core/models';
import { InfoApiService } from '@atmi/core/services/info-api.service';
import { UserService } from '@atmi/core/services/user.service';
import { generateIndexQueryString, generatePlainQueryParamsFromObject, IndexQueryStringType } from '@atmi/core/utils';
import { addOA, deleteOA, setAllOA, updateOA } from "@atmi/store/oa.repository";
import { addF, deleteF, setAllF, updateF } from "@atmi/store/bene-f.repository";
import { addS, deleteS, setAllS, updateS } from "@atmi/store/bene-s.repository";
import { addD, deleteD, setAllD, updateD } from "@atmi/store/bene-d.repository";
import { addMI, deleteMI, setAllMI, updateMI } from "@atmi/store/bene-mi.repository";
import { CollectionClassName } from '@src/app/features/collections/models/collection';

export type CardType = 'oa' | 'mi' | 'f' | 's' | 'd';
export const geteCardTypeByClassName = (className: CollectionClassName): CardType => {
  switch (className) {
    case 'BeneOA':
      return 'oa';
    case 'BeneMI':
      return 'mi';
    case 'BeneF':
      return 'f';
    case 'BeneS':
      return 's';
    case 'BeneD':
      return 'd';
    default:
      return 'oa';
  }
}
export interface CardPage<T> {
  data: T[];
  count: number;
}

/** TODO: Remove `first() operator used to avoid infinite subcriptions due to interceptors` */
@Injectable({
  providedIn: CoreModule,
})
export class CardsApiService {

  private readonly pathOA = `${this.baseUrl}/beni-oa`;

  constructor(
    @Inject(basePath) private readonly baseUrl: string,
    private readonly userApi: UserService,
    private readonly infoApi: InfoApiService,
    private readonly http: HttpClient,
  ) {
  }


  getAll() {
    return this.http.get<ResponsePaginateSuccess<cardOA.CardOA[]>>(this.pathOA, {})
      .pipe(
        map((res) => ({ data: res.data, count: res.count })),
        tap((res) => {
          setAllOA(res.data);
          return res;
        }),
        first(),
      );
  }


  indexOA(queryString?: IndexQueryStringType) {
    const params = generateIndexQueryString(queryString);
    return this.http.get<ResponsePaginateSuccess<cardOA.CardOA[]>>(this.pathOA, { params }).pipe(
      map((res) => ({ data: res.data, count: res.count })),
      first(),
    );
  }

  getOA(id: string) {
    return this.http.get<ResponseSuccess<cardOA.CardOA>>(`${this.pathOA}/${id}`).pipe(
      map((res) => res.data),
      first(),
    );
  }

  postOA(card: cardOA.CardOA) {
    return this.http.post<ResponseSuccess<cardOA.CardOA>>(this.pathOA, card)
      .pipe(
        map((res) => res.data),
        first(),
        tap((cardOa) => {
          addOA(cardOa);
          return cardOa;
        }),
      );
  }

  putOA(card: cardOA.CardOA) {
    return this.http.put<ResponseSuccess<cardOA.CardOA>>(`${this.pathOA}/${card.id}`, card).pipe(
      map((res) => res.data),
      first(),
      tap((cardOa) => {
        updateOA(cardOa.id, cardOa);
        return cardOa;
      }),
    );
  }

  removeOA(id: string) {
    return this.http.delete(`${this.pathOA}/${id}`)
      .pipe(
        first(),
        tap((res) => {
          deleteOA(id);
          return res;
        }));
  }

  removeMI(id: string, type: CardType = 'mi') {
    const url = `${this.baseUrl}/beni-${type}`;
    return this.http.delete(`${url}/${id}`).pipe(
      tap(() => {
        deleteMI(id);
      }),
      first(),
    );
  }

  removeS(id: string, type: CardType = 's') {
    const url = `${this.baseUrl}/beni-${type}`;
    return this.http.delete(`${url}/${id}`).pipe(
      tap(() => {
        deleteS(id);
      }),
      first(),
    );
  }

  removeF(id: string, type: CardType = 'f') {
    const url = `${this.baseUrl}/beni-${type}`;
    return this.http.delete(`${url}/${id}`).pipe(
      tap(() => {
        deleteF(id);
      }),
      first(),
    );
  }

  removeD(id: string, type: CardType = 'd') {
    const url = `${this.baseUrl}/beni-${type}`;
    return this.http.delete(`${url}/${id}`).pipe(
      tap(() => {
        deleteD(id);
      }),
      first(),
    );
  }

  index(type: 'mi', queryString?: IndexQueryStringType): Observable<CardPage<CardMI>>;
  index(type: CardType = 'oa', queryString?: IndexQueryStringType) {
    const params = generateIndexQueryString(queryString);
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.get<ResponsePaginateSuccess<cardOA.CardOA[]>>(url, { params }).pipe(
      map((res) => ({ data: res.data, count: res.count })),
      first(),
    );
  }

  indexMI(queryString?: IndexQueryStringType, type = 'mi'): Observable<CardPage<CardMI>> {
    const params = generateIndexQueryString(queryString);
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.get<ResponsePaginateSuccess<cardMI.CardMI[]>>(url, { params }).pipe(
      map((res) => ({ data: res.data, count: res.count })),
      first(),
    );
  }

  indexF(queryString?: IndexQueryStringType, type = 'f'): Observable<CardPage<cardF.CardF>> {
    const params = generateIndexQueryString(queryString);
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.get<ResponsePaginateSuccess<cardF.CardF[]>>(url, { params }).pipe(
      map((res) => ({ data: res.data, count: res.count })),
      first(),
    );
  }

  indexS(queryString: IndexQueryStringType, type = 's'): Observable<CardPage<cardS.CardS>> {
    const params = generateIndexQueryString(queryString);
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.get<ResponsePaginateSuccess<cardS.CardS[]>>(url, { params }).pipe(
      map((res) => ({ data: res.data, count: res.count })),
      first(),
    );
  }

  indexD(queryString: IndexQueryStringType, type: CardType = 'd'): Observable<CardPage<CardD>> {
    const params = generateIndexQueryString(queryString);
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.get<ResponsePaginateSuccess<CardD[]>>(url, { params }).pipe(
      map((res) => ({ data: res.data, count: res.count })),
      first(),
    );
  }

  get(id: string, type: CardType = 'oa') {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.get<ResponseSuccess<cardOA.CardOA>>(`${url}/${id}`).pipe(
      map((res) => res.data),
      first(),
    );
  }

  getF(id: string, type = 'f'): Observable<cardF.CardF> {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.get<ResponseSuccess<cardF.CardF>>(`${url}/${id}`).pipe(
      map((res) => res.data),
      first(),
    );
  }

  getS(id: string, type = 's'): Observable<cardS.CardS> {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.get<ResponseSuccess<cardS.CardS>>(`${url}/${id}`).pipe(
      map((res) => res.data),
      first(),
    );
  }

  getD(id: string, type = 'd'): Observable<CardD> {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.get<ResponseSuccess<CardD>>(`${url}/${id}`).pipe(
      map((res) => res.data),
      first(),
    );
  }

  getMI(id: string, type = 'mi'): Observable<CardMI> {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.get<ResponseSuccess<CardMI>>(`${url}/${id}`).pipe(
      map((res) => res.data),
    );
  }

  post(card: CardMI, type: 'mi'): Observable<CardMI>;
  post(card: cardOA.CardOA | CardMI, type: CardType = 'oa') {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.post<ResponseSuccess<cardOA.CardOA | CardMI>>(url, card).pipe(
      map((res) => res.data),
      tap((result) => {
        if (type === 'mi') {
          addMI(result as CardMI);
        }
        return result;
      }),
      first(),
    );
  }

  postF(card: cardF.CardF, type: 'f'): Observable<cardF.CardF> {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.post<ResponseSuccess<cardF.CardF>>(url, card).pipe(
      map((res) => res.data),
      tap((cardF) => {
        addF(cardF);
        return cardF;
      }),
      first(),
    );
  }


  postS(card: cardS.CardS, type: 's'): Observable<cardS.CardS> {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.post<ResponseSuccess<cardS.CardS>>(url, card).pipe(
      map((res) => res.data),
      tap((cardS) => {
        addS(cardS);
        return cardS;
      }),
      first(),
    );
  }

  postD(card: CardD, type = 'd'): Observable<CardD> {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.post<ResponseSuccess<CardD>>(url, card).pipe(
      map((res) => res.data),
      tap((cardD) => {
        addD(cardD);
        return cardD;
      }),
      first(),
    );
  }

  put(card: CardMI, type: 'mi'): Observable<CardMI>;
  put(card: cardOA.CardOA | CardMI, type: CardType = 'oa') {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.put<ResponseSuccess<cardOA.CardOA | CardMI>>(`${url}/${card.id}`, card).pipe(
      map((res) => res.data),
      tap((result) => {
        if (type === 'mi') {
          updateMI(result.id, result as CardMI);
        }
        return result;
      }),
      first(),
    );
  }

  putF(card: cardF.CardF, type: 'f'): Observable<cardF.CardF> {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.put<ResponseSuccess<cardF.CardF>>(`${url}/${card.id}`, card).pipe(
      map((res) => res.data),
      tap((cardF) => {
        updateF(cardF.id, cardF);
        return cardF;
      }),
      first(),
    );
  }

  putS(card: cardS.CardS, type: 's'): Observable<cardS.CardS> {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.put<ResponseSuccess<cardS.CardS>>(`${url}/${card.id}`, card).pipe(
      map((res) => res.data),
      tap((cardS) => {
        updateS(cardS.id, cardS);
        return cardS;
      }),
      first(),
    );
  }

  putD(card: CardD, type = 'd'): Observable<CardD> {
    const url = `${this.baseUrl}/beni-${type}`;

    return this.http.put<ResponseSuccess<CardD>>(`${url}/${card.id}`, card).pipe(
      map((res) => res.data),
      tap((cardD) => {
        updateD(cardD.id, cardD);
        return cardD;
      }),
      first(),
    );
  }

  getCardSourceBibs(id: string, type: CardType) {
    const url = `${this.baseUrl}/beni-${type}/${id}/fonti-bib`;

    return this.http.get<ResponseSuccess<SourceBibCard[]>>(url).pipe(
      map((res) => res.data),
      first(),
    );
  }

  exportCard(
    id: string,
    type: CardType,
    template: string = 'default',
    format: string = 'pdf',
  ) {
    const key = this.binaryKeyGenerator();

    let url = `${this.baseUrl}/beni-${type}/${id}/export?template=${template}&format=${format}`;
    if (key) {
      url += `&key=${key}`;
    }

    return this.http.get(url, { responseType: 'blob' });
  }

  exportList(
    type: CardType,
    template: string = 'default',
    format: string = 'pdf',
    queryString: IndexQueryStringType,
    selection?: string[],
  ) {
    const key = this.binaryKeyGenerator();
    const params = generatePlainQueryParamsFromObject({ ...queryString, selection });

    let url = `${this.baseUrl}/beni-${type}/export?template=${template}&format=${format}${params}`;
    if (key) {
      url += `&key=${key}`;
    }

    return this.http.get(url, { responseType: 'blob' });
  }

  private binaryKeyGenerator() {
    const token = this.userApi.selectStateSnasphot().token;
    const info = this.infoApi.selectStateSnapshot().info;
    const ipAddress = info && info.ip_address;

    const key = `${token}${ipAddress}`;
    const keyHashed = Md5.hashStr(key) as string;
    return keyHashed;
  }
}
